import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/lib/i18n';
import { useSalonProfile } from '@/contexts/SalonProfileContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, ChevronLeft, ChevronRight, Clock, User, Scissors, CheckCircle, XCircle, AlertTriangle, Pencil, CalendarDays, ExternalLink, Search, X, Users } from 'lucide-react';
import { format, addDays, subDays, startOfDay, parseISO } from 'date-fns';

const statusConfig = {
  scheduled: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
  completed: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
  cancelled: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
  no_show: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: XCircle },
};

const timeSlots = ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00'];

const emptyForm = {
  customer_name: '',
  customer_id: '',
  customer_phone: '',
  service_names: [],
  staff_id: '',
  staff_name: '',
  time_slot: '10:00',
  notes: '',
};

export default function Bookings() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [cancelConfirmId, setCancelConfirmId] = useState(null);
  const [noShowConfirmId, setNoShowConfirmId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const { t } = useLanguage();
  const { profile } = useSalonProfile();
  const location = useLocation();
  const navigate = useNavigate();
  const preselectedCustomer = location.state?.customer ?? null;

  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/bookings?date=${dateStr}`);
      setBookings(r.data || []);
    } catch {
      toast.error(t('book_load_error'));
    } finally {
      setLoading(false);
    }
  }, [dateStr, t]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  useEffect(() => {
    setActiveFilter('all');
  }, [dateStr]);

  useEffect(() => {
    Promise.all([api.get('/services'), api.get('/staff'), api.get('/customers')])
      .then(([s, st, c]) => {
        setServices((s.data || []).filter((x) => x.active));
        setStaffList(st.data || []);
        setCustomers(c.data || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (preselectedCustomer && preselectedCustomer.id) {
      setForm((f) => ({
        ...f,
        customer_id: preselectedCustomer.id,
        customer_name: preselectedCustomer.name || '',
      }));
      setCustomerSearch(preselectedCustomer.name || '');
      setDialogOpen(true);
    }
  }, [preselectedCustomer]);

  const isPastDate = selectedDate < startOfDay(new Date());
  const isPastTimeToday =
    dateStr === todayStr &&
    form.time_slot &&
    (() => {
      const [h, m] = form.time_slot.split(':').map(Number);
      const slotMs = h * 60 * 60 * 1000 + m * 60 * 1000;
      const now = new Date();
      const nowMs = now.getHours() * 60 * 60 * 1000 + now.getMinutes() * 60 * 1000;
      return slotMs < nowMs - 60 * 60 * 1000;
    })();

  const showPastWarning = isPastDate || isPastTimeToday;

  const isNewCustomer = !form.customer_id && (form.customer_name || '').trim().length > 0;

  const handleSave = async () => {
    const err = {};
    if (!(form.customer_name || '').trim() && !form.customer_id) err.customer = t('val_customer_or_walkin');
    if (isNewCustomer && !(form.customer_phone || '').trim()) err.customer_phone = t('booking_new_customer_phone_required');
    if (form.service_names.length === 0) err.services = t('val_services_one');
    if (Object.keys(err).length > 0) {
      setFieldErrors(err);
      toast.error(t('book_req'));
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/bookings/${editingId}`, { ...form, date: dateStr });
        toast.success(t('book_status_updated', { status: 'updated' }));
      } else {
        await api.post('/bookings', { ...form, date: dateStr });
        toast.success(t('book_created'));
      }
      setDialogOpen(false);
      setForm({ ...emptyForm, customer_phone: '' });
      setEditingId(null);
      setCustomerSearch('');
      loadBookings();
      if (!editingId) {
        api.get('/customers').then((r) => setCustomers(r.data || [])).catch(() => {});
        if (profile?.auto_send_booking_confirmation !== false) {
          toast.info(t('inv_whatsapp_alert'), { description: t('book_whatsapp_confirm') });
        }
      }
    } catch (e) {
      toast.error(editingId ? t('book_status_error') : t('book_create_error'));
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status) => {
    setStatusUpdatingId(id);
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success(t('book_status_updated', { status: status.toLowerCase() }));
      loadBookings();
      setCancelConfirmId(null);
      setNoShowConfirmId(null);
    } catch {
      toast.error(t('book_status_error'));
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleComplete = (id) => updateStatus(id, 'completed');
  const handleNoShow = (id) => setNoShowConfirmId(id);
  const handleCancelClick = (id) => setCancelConfirmId(id);
  const confirmNoShow = () => noShowConfirmId && updateStatus(noShowConfirmId, 'no_show');
  const confirmCancel = () => cancelConfirmId && updateStatus(cancelConfirmId, 'cancelled');

  const toggleService = (svcName) => {
    setForm((f) => ({
      ...f,
      service_names: f.service_names.includes(svcName)
        ? f.service_names.filter((n) => n !== svcName)
        : [...f.service_names, svcName],
    }));
  };

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setCustomerSearch('');
    setFieldErrors({});
    setDialogOpen(true);
  };

  const openEdit = (b) => {
    setEditingId(b.id);
    if (b.date) {
      try {
        const d = parseISO(b.date);
        if (!isNaN(d.getTime())) setSelectedDate(d);
      } catch (_) {}
    }
    setForm({
      customer_name: b.customer_name || '',
      customer_id: b.customer_id || '',
      customer_phone: '',
      service_names: b.service_names || [],
      staff_id: b.staff_id || '',
      staff_name: b.staff_name || '',
      time_slot: b.time_slot || '10:00',
      notes: b.notes || '',
    });
    setCustomerSearch(b.customer_name || '');
    setFieldErrors({});
    setDialogOpen(true);
  };

  const filteredCustomers =
    customerSearch.length >= 2
      ? customers.filter(
          (c) =>
            c.name.toLowerCase().includes(customerSearch.toLowerCase()) || (c.phone || '').includes(customerSearch)
        )
      : [];

  const selectedCustomer = form.customer_id ? customers.find((c) => c.id === form.customer_id) : null;
  const customerSelected = !!form.customer_id;
  const hasCustomerSearchText = (customerSearch || '').trim().length > 0;
  const showCustomerDropdown = !customerSelected && customerSearch.length >= 2 && filteredCustomers.length > 0;
  const showNewCustomerPhone =
    !customerSelected && customerSearch.length >= 2 && hasCustomerSearchText && filteredCustomers.length === 0 && !((form.customer_name || '').trim().toLowerCase() === 'walk-in');

  const selectedServiceMeta = services.filter((s) => form.service_names.includes(s.name));
  const selectedServicesTotal = selectedServiceMeta.reduce((sum, s) => sum + (Number(s.price) || 0), 0);

  const isPastSlot = (slot) => {
    if (dateStr !== todayStr) return false;
    if (!slot) return false;
    const [h, m] = slot.split(':').map(Number);
    const slotMs = h * 60 * 60 * 1000 + m * 60 * 1000;
    const now = new Date();
    const nowMs = now.getHours() * 60 * 60 * 1000 + now.getMinutes() * 60 * 1000;
    return slotMs < nowMs;
  };

  const getStatusLabel = (status) => {
    const key = (status || '').toLowerCase();
    return t('booking_' + (statusConfig[key] ? key : 'scheduled'));
  };

  const truncateNote = (note, max = 60) => {
    if (!note || !note.trim()) return '';
    const s = note.trim();
    return s.length <= max ? s : s.slice(0, max) + '...';
  };

  const filteredBookings = bookings.filter((b) => {
    const s = (b.status || 'scheduled').toLowerCase();
    if (activeFilter === 'all') return true;
    if (activeFilter === 'scheduled') return s === 'scheduled';
    if (activeFilter === 'completed') return s === 'completed';
    if (activeFilter === 'cancelled') return s === 'cancelled';
    if (activeFilter === 'no_show') return s === 'no_show';
    return true;
  });

  const counts = {
    all: bookings.length,
    scheduled: bookings.filter((b) => (b.status || '').toLowerCase() === 'scheduled').length,
    completed: bookings.filter((b) => (b.status || '').toLowerCase() === 'completed').length,
    cancelled: bookings.filter((b) => (b.status || '').toLowerCase() === 'cancelled').length,
    no_show: bookings.filter((b) => (b.status || '').toLowerCase() === 'no_show').length,
  };

  return (
    <div className="space-y-6">
      <style>{`.bookings-filter-scroll::-webkit-scrollbar { display: none }`}</style>
      {/* Date Navigation: desktop single row [<][Date][>][Today] ←→ [+ New Booking]; mobile two rows */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center justify-center gap-2 md:justify-start md:gap-3">
          <Button variant="outline" size="icon" className="min-w-[44px] min-h-[44px] w-11 h-11 md:w-12 md:h-12 rounded-xl flex-shrink-0" onClick={() => setSelectedDate((d) => subDays(d, 1))} data-testid="prev-day-btn">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <button
            data-testid="date-picker-btn"
            onClick={() => setShowCalendar(!showCalendar)}
            className="font-poppins text-base md:text-xl font-semibold text-[#1a1a2e] hover:text-[#f0a500] transition-colors px-2 py-2 text-center min-w-0 flex-1 md:flex-initial"
          >
            {format(selectedDate, 'EEEE, MMM d, yyyy')}
          </button>
          <Button variant="outline" size="icon" className="min-w-[44px] min-h-[44px] w-11 h-11 md:w-12 md:h-12 rounded-xl flex-shrink-0" onClick={() => setSelectedDate((d) => addDays(d, 1))} data-testid="next-day-btn">
            <ChevronRight className="w-5 h-5" />
          </Button>
          <Button variant="ghost" className="text-[#f0a500] font-medium min-h-[44px] px-3 md:px-2" onClick={() => setSelectedDate(new Date())} data-testid="today-btn">
            {t('book_today')}
          </Button>
        </div>
        <Button data-testid="new-booking-btn" onClick={openNew} className="w-full md:w-auto h-12 md:h-14 px-6 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] text-base md:text-lg font-semibold gap-2">
          <Plus className="w-5 h-5" /> {t('book_new')}
        </Button>
      </div>

      {showCalendar && (
        <Card className="border-0 shadow-lg rounded-2xl w-fit">
          <CardContent className="p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => {
                if (d) {
                  setSelectedDate(d);
                  setShowCalendar(false);
                }
              }}
              className="rounded-xl"
            />
          </CardContent>
        </Card>
      )}

      {/* Filter tabs — between date nav and first booking card */}
      {!loading && (
        <div
          className="bookings-filter-scroll overflow-x-auto overflow-y-hidden flex gap-2 py-3"
          style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
        >
          {['all', 'scheduled', 'completed', 'cancelled', 'no_show'].map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => setActiveFilter(filter)}
              className="flex-shrink-0 px-3.5 py-1.5 rounded-full border text-sm whitespace-nowrap cursor-pointer transition-colors"
              style={{
                fontWeight: activeFilter === filter ? 600 : 400,
                background: activeFilter === filter ? '#1a1a2e' : 'transparent',
                color: activeFilter === filter ? 'white' : '#666',
                borderColor: activeFilter === filter ? '#1a1a2e' : '#ddd',
              }}
            >
              {t(`booking_filter_${filter}`)}
              {counts[filter] > 0 && ` (${counts[filter]})`}
            </button>
          ))}
        </div>
      )}

      {/* Bookings List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
      ) : bookings.length === 0 ? (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="py-16 text-center">
            <div className="flex justify-center mb-4">
              <CalendarDays className="w-16 h-16 text-gray-300" />
            </div>
            <p className="text-gray-500 text-lg mb-4">{t('no_bookings_today', { date: format(selectedDate, 'MMM d, yyyy') })}</p>
            <Button onClick={openNew} className="h-12 px-6 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] font-semibold gap-2" data-testid="empty-new-booking-btn">
              <Plus className="w-5 h-5" /> {t('book_new')}
            </Button>
          </CardContent>
        </Card>
      ) : filteredBookings.length === 0 ? (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="py-16 text-center">
            <div className="flex justify-center mb-4">
              <CalendarDays className="w-16 h-16 text-gray-300" />
            </div>
            <p className="text-gray-500 text-lg mb-4">{t('no_filtered_bookings', { status: t(`booking_filter_${activeFilter}`), date: format(selectedDate, 'MMM d, yyyy') })}</p>
            <Button onClick={() => setActiveFilter('all')} variant="outline" className="h-12 px-6 rounded-xl gap-2">
              {t('booking_filter_all')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredBookings.map((b) => {
            const status = (b.status || 'scheduled').toLowerCase();
            const sc = statusConfig[status] || statusConfig.scheduled;
            const isScheduled = status === 'scheduled';
            const isCompleted = status === 'completed';
            const isMuted = status === 'cancelled' || status === 'no_show';

            return (
              <Card
                key={b.id}
                className={`border-0 shadow-sm rounded-2xl transition-all ${isMuted ? 'opacity-75 bg-gray-50' : 'hover:shadow-md'}`}
              >
                <CardContent className="p-5">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="bg-[#1a1a2e] text-white w-14 h-14 rounded-2xl flex flex-col items-center justify-center flex-shrink-0">
                          <span className="text-lg font-bold leading-none">{b.time_slot?.split(':')[0]}</span>
                          <span className="text-xs opacity-70">{b.time_slot?.split(':')[1] || '00'}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-lg text-[#1a1a2e]">{b.customer_name}</h3>
                            <Badge className={`${sc.color} border text-xs`}>{getStatusLabel(b.status)}</Badge>
                          </div>
                          <p className="text-gray-500 text-sm mt-1 flex items-center gap-1">
                            <Scissors className="w-3.5 h-3.5 flex-shrink-0" /> {b.service_names?.join(', ')}
                          </p>
                          {b.notes && b.notes.trim() && (
                            <p
                              className="text-gray-400 text-sm mt-0.5 italic truncate max-w-full"
                              title={b.notes.trim()}
                            >
                              📝 {truncateNote(b.notes)}
                            </p>
                          )}
                          {b.staff_name && (
                            <p className="text-gray-400 text-sm flex items-center gap-1 mt-0.5">
                              <User className="w-3.5 h-3.5" /> {b.staff_name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:justify-end w-full sm:w-auto">
                        {isScheduled && (
                          <>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                data-testid={`complete-booking-${b.id}`}
                                onClick={() => handleComplete(b.id)}
                                disabled={statusUpdatingId === b.id}
                                className="min-h-[44px] h-11 sm:h-10 rounded-xl bg-green-500 hover:bg-green-600 text-white gap-1 text-sm px-4 flex-1 sm:flex-initial"
                              >
                                <CheckCircle className="w-4 h-4" /> {statusUpdatingId === b.id ? t('saving') : t('booking_complete_btn')}
                              </Button>
                              <Button
                                data-testid={`noshow-booking-${b.id}`}
                                onClick={() => handleNoShow(b.id)}
                                disabled={statusUpdatingId === b.id}
                                className="min-h-[44px] h-11 sm:h-10 rounded-xl bg-amber-500 hover:bg-amber-600 text-white gap-1 text-sm px-4 flex-1 sm:flex-initial"
                              >
                                <XCircle className="w-4 h-4" /> {t('booking_no_show_btn')}
                              </Button>
                              <Button
                                data-testid={`cancel-booking-${b.id}`}
                                onClick={() => handleCancelClick(b.id)}
                                disabled={statusUpdatingId === b.id}
                                variant="outline"
                                className="min-h-[44px] h-11 sm:h-10 rounded-xl text-red-500 border-red-200 hover:bg-red-50 gap-1 text-sm px-4 w-full sm:w-auto"
                              >
                                <XCircle className="w-4 h-4" /> {t('booking_cancel_btn')}
                              </Button>
                            </div>
                            <Button
                              data-testid={`edit-booking-${b.id}`}
                              onClick={() => openEdit(b)}
                              variant="ghost"
                              size="icon"
                              className="min-w-[44px] min-h-[44px] w-11 h-11 sm:w-10 sm:h-10 rounded-xl text-[#1a1a2e] hover:bg-gray-100 self-start sm:self-center"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {isCompleted && (
                          <Button
                            variant="link"
                            className="text-[#f0a500] font-medium gap-1 h-auto p-0"
                            onClick={() => navigate('/billing', { state: { customer: { id: b.customer_id, name: b.customer_name } } })}
                          >
                            {t('booking_create_bill')} <ExternalLink className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* New / Edit Booking Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingId(null); setForm(emptyForm); setCustomerSearch(''); } }}>
        <DialogContent className="booking-modal booking-modal--animate bg-[#faf8f5] rounded-2xl sm:rounded-3xl max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] p-0 overflow-hidden">
          <style>{`
            .booking-modal--animate { animation: bookingModalIn 200ms ease-out both; transform-origin: center; }
            @keyframes bookingModalIn { from { opacity: 0; transform: translateY(6px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
            .booking-modal__scroll { scrollbar-width: thin; scrollbar-color: #e5e7eb transparent; }
            .booking-modal__scroll::-webkit-scrollbar { width: 8px; }
            .booking-modal__scroll::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 999px; }
            .booking-modal__dropdown { animation: bookingDropdownIn 150ms ease-out both; }
            @keyframes bookingDropdownIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
            .booking-customer-card { animation: bookingCustomerCardIn 200ms ease-out both; }
            @keyframes bookingCustomerCardIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
            .booking-phone-wrap { transition: max-height 200ms ease, opacity 200ms ease; overflow: hidden; }
          `}</style>

          <div className="flex flex-col max-h-[90vh]">
            <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-black/5 bg-[#faf8f5]">
              <DialogHeader className="space-y-1">
                <div className="flex items-start justify-between gap-3">
                  <DialogTitle className="font-poppins text-lg sm:text-xl flex items-center gap-2 text-[#1a1a2e]">
                    <CalendarDays className="w-5 h-5 text-[#f5a623]" />
                    {editingId ? t('edit_booking_title') : t('book_new')}
                  </DialogTitle>
                </div>
                <p className="text-sm text-gray-500">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</p>
              </DialogHeader>

              {showPastWarning && (
                <div className="mt-3 rounded-lg bg-[#FFF3CD] border border-[#FDE8B4] px-3 py-2.5 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-amber-900">
                      {isPastDate ? t('booking_past_date_warning') : t('booking_past_time_warning')}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="booking-modal__scroll flex-1 overflow-y-auto px-4 sm:px-6 py-4">
              <div className="space-y-5">
                {/* Customer */}
                <div className="pb-4 border-b border-black/5">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <Label className="text-base font-semibold text-[#1a1a2e]">
                      {t('book_customer')} <span className="text-[#f5a623]">*</span>
                    </Label>
                    {!customerSelected && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-8 px-2 text-xs text-gray-500 hover:text-[#1a1a2e]"
                        onClick={() => {
                          setCustomerSearch('');
                          setForm((f) => ({ ...f, customer_name: 'Walk-in', customer_id: '', customer_phone: '' }));
                        }}
                      >
                        {t('bill_walkin')}
                      </Button>
                    )}
                  </div>

                  {customerSelected ? (
                    <div className="booking-customer-card rounded-[10px] border border-amber-200 bg-[#FFF8ED] pl-3 pr-3 py-3 relative">
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#f5a623] rounded-l-[10px]" />
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1a1a2e] text-white flex items-center justify-center font-semibold flex-shrink-0">
                          {(selectedCustomer?.name || form.customer_name || '').trim().slice(0, 1).toUpperCase() || 'C'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-semibold text-[#1a1a2e] truncate">{selectedCustomer?.name || form.customer_name}</p>
                            <Badge className="bg-[#f5a623]/15 text-[#1a1a2e] border border-[#f5a623]/30">
                              {selectedCustomer?.loyalty_tier || 'Bronze'}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mt-0.5 flex items-center gap-1">
                            <span aria-hidden>📞</span> {selectedCustomer?.phone || ''}
                          </p>
                          <p className="text-xs text-[#b7791f] mt-1">
                            {(selectedCustomer?.loyalty_points ?? 0).toLocaleString()} {t('bill_loyalty')}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          className="h-8 px-2.5 rounded-lg text-xs border-amber-300 text-[#1a1a2e] hover:bg-amber-50"
                          onClick={() => {
                            setCustomerSearch('');
                            setForm((f) => ({ ...f, customer_id: '', customer_name: '', customer_phone: '' }));
                          }}
                        >
                          {t('booking_edit_btn')} <X className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </div>
                    </div>
                  ) : (form.customer_name || '').trim().toLowerCase() === 'walk-in' ? (
                    <div className="booking-customer-card rounded-[10px] border border-gray-200 bg-gray-50 pl-3 pr-3 py-3 relative">
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-300 rounded-l-[10px]" />
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-300 text-gray-700 flex items-center justify-center font-semibold flex-shrink-0">
                          <span aria-hidden>🚶</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-[#1a1a2e]">Walk-in</p>
                          <p className="text-sm text-gray-500 mt-0.5">{t('served_by_optional')}</p>
                          <p className="text-xs text-gray-400 mt-1">{t('val_customer_or_walkin')}</p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          className="h-8 px-2.5 rounded-lg text-xs border-gray-300 text-[#1a1a2e] hover:bg-white"
                          onClick={() => {
                            setCustomerSearch('');
                            setForm((f) => ({ ...f, customer_name: '', customer_id: '', customer_phone: '' }));
                          }}
                        >
                          {t('booking_edit_btn')} <X className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="relative">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <Input
                          data-testid="booking-customer-search"
                          placeholder={t('book_search_cust')}
                          className="h-12 rounded-xl pl-9 pr-10 focus-visible:ring-1 focus-visible:ring-[#f5a623] border-gray-200 bg-white"
                          value={customerSearch}
                          onChange={(e) => {
                            setCustomerSearch(e.target.value);
                            setForm((f) => ({ ...f, customer_name: e.target.value, customer_id: '' }));
                          }}
                        />
                        {hasCustomerSearchText && (
                          <button
                            type="button"
                            aria-label="Clear"
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"
                            onClick={() => {
                              setCustomerSearch('');
                              setForm((f) => ({ ...f, customer_name: '', customer_id: '', customer_phone: '' }));
                            }}
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        )}
                      </div>

                      {fieldErrors.customer && <p className="text-red-500 text-sm mt-1">{fieldErrors.customer}</p>}

                      <div
                        className="booking-phone-wrap mt-3"
                        style={{
                          maxHeight: showNewCustomerPhone ? 120 : 0,
                          opacity: showNewCustomerPhone ? 1 : 0,
                        }}
                        aria-hidden={!showNewCustomerPhone}
                      >
                        {showNewCustomerPhone && (
                          <div>
                            <Label className="text-base mb-1.5 block">
                              {t('booking_new_customer_phone')} <span className="text-[#f5a623]">*</span>
                            </Label>
                            <Input
                              data-testid="booking-customer-phone"
                              placeholder={t('booking_new_customer_phone_placeholder')}
                              className="h-12 rounded-xl border-gray-200 bg-white focus-visible:ring-1 focus-visible:ring-[#f5a623]"
                              value={form.customer_phone}
                              onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                            />
                            {fieldErrors.customer_phone && <p className="text-red-500 text-sm mt-1">{fieldErrors.customer_phone}</p>}
                          </div>
                        )}
                      </div>

                      {showCustomerDropdown && (
                        <div className="booking-modal__dropdown mt-2 bg-white border border-gray-100 rounded-xl shadow-md overflow-hidden">
                          {filteredCustomers.slice(0, 5).map((c) => (
                            <button
                              key={c.id}
                              data-testid={`booking-customer-${c.id}`}
                              type="button"
                              onClick={() => {
                                setForm((f) => ({ ...f, customer_name: c.name, customer_id: c.id, customer_phone: '' }));
                                setCustomerSearch(c.name);
                              }}
                              className="group w-full text-left px-3 py-2.5 text-sm border-b last:border-0 hover:bg-[#fffaf2] relative"
                            >
                              <span className="absolute left-0 top-0 bottom-0 w-1 bg-transparent group-hover:bg-[#f5a623]" />
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center font-semibold flex-shrink-0">
                                  {(c.name || '').trim().slice(0, 1).toUpperCase() || 'C'}
                                </div>
                                <div className="min-w-0 flex-1 flex items-center justify-between gap-3">
                                  <span className="font-medium text-[#1a1a2e] truncate">{c.name}</span>
                                  <span className="text-gray-400 flex-shrink-0">{c.phone}</span>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Services */}
                <div className="pb-4 border-b border-black/5">
                  <Label className="text-base font-semibold text-[#1a1a2e] block mb-2">
                    {t('book_services')} <span className="text-[#f5a623]">*</span>
                  </Label>
                  {fieldErrors.services && <p className="text-red-500 text-sm mb-2">{fieldErrors.services}</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {services.map((s) => {
                      const selected = form.service_names.includes(s.name);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          data-testid={`booking-svc-${s.id}`}
                          onClick={() => toggleService(s.name)}
                          className={`w-full text-left rounded-lg border px-3.5 py-2.5 transition-all ${
                            selected
                              ? 'bg-[#f5a623] text-[#1a1a2e] border-[#f5a623] shadow-sm transform scale-[1.02]'
                              : 'bg-white border-gray-200 text-[#1a1a2e] hover:border-[#f5a623]'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 flex items-center gap-2">
                              {selected && <span className="font-semibold">✓</span>}
                              <span className="font-medium truncate">{s.name}</span>
                            </div>
                            <span className={`${selected ? 'text-[#1a1a2e]' : 'text-gray-400'} flex-shrink-0`}>₹{Number(s.price) || 0}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {form.service_names.length > 0 && (
                    <p className="mt-2 text-xs text-gray-500 italic">
                      {form.service_names.length} {t('book_services')} · ₹{selectedServicesTotal} {t('total')}
                    </p>
                  )}
                </div>

                {/* Stylist */}
                <div className="pb-4 border-b border-black/5">
                  <Label className="text-base font-semibold text-[#1a1a2e] block mb-2">{t('book_stylist')}</Label>
                  <Select
                    value={form.staff_id || '__any__'}
                    onValueChange={(value) => {
                      const mapped = value === '__any__' ? '' : value;
                      const staff = staffList.find((s) => s.id === mapped);
                      setForm((f) => ({ ...f, staff_id: mapped, staff_name: staff?.name || '' }));
                    }}
                  >
                    <SelectTrigger data-testid="booking-staff-select" className="h-12 rounded-xl border-gray-200 bg-white focus:ring-[#f5a623]">
                      <SelectValue placeholder={t('book_any')} />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl border-gray-100 shadow-lg booking-modal__dropdown">
                      <SelectItem value="__any__">
                        <span className="inline-flex items-center gap-2">
                          <Users className="w-4 h-4 text-gray-500" />
                          {t('book_any')}
                        </span>
                      </SelectItem>
                      {staffList.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          <span className="inline-flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-xs font-semibold">
                              {(s.name || '').trim().slice(0, 1).toUpperCase() || 'S'}
                            </span>
                            <span className="truncate">{s.name}</span>
                            {s.role ? <span className="text-gray-400">({s.role})</span> : null}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Time Slot */}
                <div className="pb-4 border-b border-black/5">
                  <Label className="text-base font-semibold text-[#1a1a2e] block mb-2">{t('book_time')}</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {timeSlots.map((slot) => {
                      const selected = form.time_slot === slot;
                      const past = isPastSlot(slot);
                      return (
                        <button
                          key={slot}
                          type="button"
                          data-testid={`time-slot-${slot}`}
                          onClick={() => {
                            if (past) return;
                            setForm((f) => ({ ...f, time_slot: slot }));
                          }}
                          disabled={past}
                          className={`h-10 rounded-lg text-sm font-medium transition-all ${
                            past
                              ? 'bg-white border border-gray-100 text-gray-400 line-through cursor-not-allowed'
                              : selected
                                ? 'bg-[#1a1a2e] text-white font-semibold'
                                : 'bg-white border border-gray-200 text-[#1a1a2e] hover:border-[#f5a623]'
                          }`}
                        >
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label className="text-base font-semibold text-[#1a1a2e] block mb-2">{t('book_notes')}</Label>
                  <Textarea
                    data-testid="booking-notes"
                    className="min-h-[80px] resize-y rounded-xl border-gray-200 bg-white focus-visible:ring-1 focus-visible:ring-[#f5a623]"
                    placeholder={t('book_special')}
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <div className="px-4 sm:px-6 py-4 border-t border-black/5 bg-[#faf8f5]">
              <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="h-11 sm:h-12 px-6 rounded-xl text-base w-full sm:w-auto border-gray-300"
                >
                  {t('cancel')}
                </Button>
                <Button
                  data-testid="save-booking-btn"
                  onClick={handleSave}
                  disabled={saving}
                  className="h-11 sm:h-12 px-8 rounded-xl text-base w-full sm:w-auto bg-[#f5a623] hover:bg-[#e79a18] text-[#1a1a2e] font-semibold gap-2"
                >
                  <span className="inline-flex items-center gap-2">
                    <span aria-hidden>✓</span>
                    {saving ? t('saving') : editingId ? t('booking_update_btn') : t('book_create')}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation */}
      <AlertDialog open={!!cancelConfirmId} onOpenChange={() => setCancelConfirmId(null)}>
        <AlertDialogContent className="rounded-2xl max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-poppins">{t('booking_cancel_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('booking_cancel_confirm_body')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t('booking_keep_btn')}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-cancel-booking-btn"
              onClick={confirmCancel}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
            >
              {t('booking_cancel_confirm_btn')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* No Show confirmation */}
      <AlertDialog open={!!noShowConfirmId} onOpenChange={() => setNoShowConfirmId(null)}>
        <AlertDialogContent className="rounded-2xl max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-poppins">{t('booking_noshow_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {noShowConfirmId
                ? t('booking_noshow_confirm_body', {
                    name: bookings.find((x) => x.id === noShowConfirmId)?.customer_name || '',
                  })
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-noshow-booking-btn"
              onClick={confirmNoShow}
              className="bg-amber-500 hover:bg-amber-600 rounded-xl"
            >
              {t('booking_noshow_confirm_btn')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
